import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type MekkeSpotResponseDto,
  type CreateMekkeSpotDto,
  type UpdateMekkeSpotDto,
  type MekkeSpotStatus,
  type MekkeSpotCategory,
  type MekkeSpotImageDto,
} from "@tabipla/shared";

// Query key factory
const spotKeys = {
  all: ["mekke-spots"] as const,
  lists: () => [...spotKeys.all, "list"] as const,
  list: (filters: { status?: MekkeSpotStatus; category?: MekkeSpotCategory }) =>
    [...spotKeys.lists(), filters] as const,
  details: () => [...spotKeys.all, "detail"] as const,
  detail: (id: string) => [...spotKeys.details(), id] as const,
};

// Fetch all spots for the current user
interface UseSpotsOptions {
  status?: MekkeSpotStatus | "all";
  category?: MekkeSpotCategory | "all";
}

export function useSpots(options: UseSpotsOptions = {}) {
  const { status, category } = options;

  return useQuery({
    queryKey: spotKeys.list({
      status: status === "all" ? undefined : status,
      category: category === "all" ? undefined : category,
    }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (category && category !== "all") params.set("category", category);

      const url = `/api/mekke/spots${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await apiClient.get<MekkeSpotResponseDto[]>(url);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch spots");
      }
      return response.data ?? [];
    },
  });
}

// Fetch single spot
export function useSpot(spotId: string | undefined) {
  return useQuery({
    queryKey: spotKeys.detail(spotId ?? ""),
    queryFn: async () => {
      if (!spotId) throw new Error("Spot ID is required");
      const response = await apiClient.get<MekkeSpotResponseDto>(`/api/mekke/spots/${spotId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch spot");
      }
      return response.data;
    },
    enabled: !!spotId,
  });
}

// Create spot
export function useCreateSpot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateMekkeSpotDto) => {
      const response = await apiClient.post<MekkeSpotResponseDto>("/api/mekke/spots", input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create spot");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spotKeys.all });
    },
  });
}

// Update spot
export function useUpdateSpot(spotId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateMekkeSpotDto) => {
      const response = await apiClient.put<MekkeSpotResponseDto>(
        `/api/mekke/spots/${spotId}`,
        input
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update spot");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spotKeys.all });
      queryClient.invalidateQueries({ queryKey: spotKeys.detail(spotId) });
    },
  });
}

// Delete spot
export function useDeleteSpot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (spotId: string) => {
      const response = await apiClient.delete<{ id: string }>(`/api/mekke/spots/${spotId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete spot");
      }
      return spotId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spotKeys.all });
    },
  });
}

// Add image to spot (FormData upload)
interface AddSpotImageInput {
  file: File;
  caption?: string;
}

export function useAddSpotImage(spotId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AddSpotImageInput) => {
      const formData = new FormData();
      formData.append("file", input.file);
      if (input.caption) {
        formData.append("caption", input.caption);
      }

      const response = await apiClient.upload<MekkeSpotImageDto>(
        `/api/mekke/spots/${spotId}/images`,
        formData
      );

      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to add image");
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spotKeys.detail(spotId) });
      queryClient.invalidateQueries({ queryKey: spotKeys.all });
    },
  });
}

// Remove image from spot
export function useRemoveSpotImage(spotId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (imageId: string) => {
      const response = await apiClient.delete<{ id: string }>(
        `/api/mekke/spots/${spotId}/images/${imageId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to remove image");
      }
      return imageId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spotKeys.detail(spotId) });
      queryClient.invalidateQueries({ queryKey: spotKeys.all });
    },
  });
}
